// Lightweight GA4 helper with safe guards
// Mirrors homepage event naming and only fires if gtag is available.

declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        fbq?: (...args: any[]) => void;
    }
}

export type MetaStandardEvent = 'ViewContent' | 'Lead' | 'CompleteRegistration' | 'StartTrial';

export interface MetaIdentifiedEventParams {
    phoneNumber?: string | null;
}

export function gtagEvent(eventName: string, params?: Record<string, any>) {
    try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', eventName, params ?? {});
        }
    } catch {
        // noop
    }
}

export function gtagIdentifyOwner(params: { businessId: number }) {
    gtagIdentify({ userId: `owner:${params.businessId}`, businessId: String(params.businessId) });
}

export function gtagIdentifyWorker(params: { businessId: number; workerId: number }) {
    gtagIdentify({ userId: `worker:${params.businessId}:${params.workerId}`, businessId: String(params.businessId) });
}

export function gtagIdentifyClient(params: { businessId: number; clientId: number }) {
    gtagIdentify({ userId: `client:${params.businessId}:${params.clientId}`, businessId: String(params.businessId) });
}

function gtagIdentify(params: { userId: string; businessId: string }) {
    try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('set', { user_id: params.userId });
            window.gtag('set', 'user_properties', { business_id: params.businessId });
        }
    } catch {
        // noop
    }
}

export function gtagClearIdentity() {
    try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('set', { user_id: null });
            window.gtag('set', 'user_properties', { business_id: null });
        }
    } catch {
        // noop
    }
}

function formatPhoneForMeta(phoneNumber: string) {
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length === 0) return null;
    if (digits.startsWith('55')) {
        return digits;
    }
    if (digits.length === 11) {
        return `55${digits}`;
    }
    return digits;
}

async function hashSha256(input: string) {
    try {
        if (typeof window === 'undefined' || !window.crypto?.subtle) return null;
        const encoder = new TextEncoder();
        const data = encoder.encode(input);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
        return null;
    }
}

async function hashPhoneNumber(phoneNumber: string) {
    const formatted = formatPhoneForMeta(phoneNumber);
    if (!formatted) return null;
    return await hashSha256(formatted);
}

type ContentData = {
    value?: number;
    currency?: string;
};

async function trackMetaEvent(params: {
    event: MetaStandardEvent;
    phoneNumber?: string | null;
    eventId?: string | null;
    contentData?: ContentData | null;
}) {
    if (process.env.NODE_ENV !== 'production') return;
    const { event, phoneNumber, eventId, contentData } = params;

    try {
        if (typeof window === 'undefined') return;
        const fbq = window.fbq;
        if (typeof fbq !== 'function') return;

        let hashedPhone: string | null = null;
        if (phoneNumber) {
            try {
                hashedPhone = await hashPhoneNumber(phoneNumber);
            } catch {
                // noop
            }
        }

        const options: Record<string, unknown> = {};
        if (hashedPhone) {
            options.user_data = { ph: hashedPhone };
        }
        if (eventId) {
            options.eventID = eventId;
        }
        fbq('track', event, contentData ?? {}, options);
    } catch {
        // noop
    }
}

export function trackMetaViewContent(params: { value: number }) {
    void trackMetaEvent({
        event: 'ViewContent',
        contentData: { value: params.value, currency: 'BRL' },
    });
}

export function trackMetaLead(params: MetaIdentifiedEventParams & { eventId: string }) {
    const { phoneNumber, eventId } = params;
    void trackMetaEvent({ event: 'Lead', phoneNumber, eventId });
}

export function trackMetaCompleteRegistration(params: MetaIdentifiedEventParams & { eventId: string }) {
    const { phoneNumber, eventId } = params;
    void trackMetaEvent({ event: 'CompleteRegistration', phoneNumber, eventId });
}

export function trackMetaStartTrial(params: MetaIdentifiedEventParams & { eventId: string }) {
    const { phoneNumber, eventId } = params;
    void trackMetaEvent({ event: 'StartTrial', phoneNumber, eventId });
}
